import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchAdditionalAttendance, saveAdditionalAttendanceCounts } from '../firebase/firestoreService';
import { getCurrentUser } from '../firebase/authService';
import { ATTENDANCE_LOCATIONS, getServiceType } from '../utils/roles';
import '../styles/AdditionalAttendance.css';
import { readOnlyProps } from '../utils/viewAs';

const SUNDAY_SERVICES = ['9am', '12pm', '3pm'];
const TUESDAY_SERVICES = ['tuesday_prayer'];
const SAVE_DEBOUNCE_MS = 1200;

const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getServiceLabel = (id) => {
  const s = getServiceType(id);
  return s ? s.name : id;
};

const sumCounts = (counts) => Object.values(counts || {}).reduce(
  (acc, svc) => acc + Object.values(svc || {}).reduce((a, n) => a + (Number(n) || 0), 0), 0
);

const AdditionalAttendance = ({ selectedDate, checkIns, onTotalsChange, occupancyData = [] }) => {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const day = selectedDate.getDay();
  const services = useMemo(() => {
    if (day === 0) return SUNDAY_SERVICES;
    if (day === 2) return TUESDAY_SERVICES;
    return [];
  }, [day]);

  const dateStr = toDateStr(selectedDate);
  const isServiceDay = services.length > 0;

  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [pulseKey, setPulseKey] = useState(null); // `${serviceId}|${locationId}` of last edited cell
  const [expanded, setExpanded] = useState(false);

  // Refs to avoid stale closures inside the debounce timer
  const saveTimerRef = useRef(null);
  const savedClearTimerRef = useRef(null);
  const pendingCountsRef = useRef(null);
  const lastSavedSnapshotRef = useRef('{}');
  const onTotalsChangeRef = useRef(onTotalsChange);
  const checkInsByServiceRef = useRef({});

  useEffect(() => {
    onTotalsChangeRef.current = onTotalsChange;
  }, [onTotalsChange]);

  // Count check-ins per service from the live checkIns prop
  const checkInsByService = useMemo(() => {
    const map = {};
    services.forEach((s) => { map[s] = 0; });
    (checkIns || []).forEach((c) => {
      if (c.serviceType && map.hasOwnProperty(c.serviceType)) {
        map[c.serviceType] += 1;
      }
    });
    return map;
  }, [checkIns, services]);

  useEffect(() => {
    checkInsByServiceRef.current = checkInsByService;
  }, [checkInsByService]);

  // Load data when the date changes
  useEffect(() => {
    if (!isServiceDay) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSaveState('idle');
    setErrorMsg('');
    fetchAdditionalAttendance(dateStr)
      .then((data) => {
        if (cancelled) return;
        const normalized = {};
        services.forEach((sid) => {
          normalized[sid] = {};
          ATTENDANCE_LOCATIONS.forEach((loc) => {
            normalized[sid][loc.id] = data.counts?.[sid]?.[loc.id] || 0;
          });
        });
        setCounts(normalized);
        lastSavedSnapshotRef.current = JSON.stringify(normalized);
        if (onTotalsChangeRef.current) {
          onTotalsChangeRef.current({
            total: sumCounts(normalized),
            sundayCount: data.sundayCount,
            tuesdayCount: data.tuesdayCount
          });
        }
      })
      .catch((err) => {
        console.error('Failed to load additional attendance:', err);
        if (!cancelled) {
          setErrorMsg('Could not load attendance data.');
          setSaveState('error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, isServiceDay, services.join(',')]);

  const performSave = useCallback(async (countsToSave) => {
    setSaveState('saving');
    setErrorMsg('');
    try {
      const user = getCurrentUser();
      const result = await saveAdditionalAttendanceCounts(
        dateStr,
        countsToSave,
        user.uid,
        user.displayName || user.email
      );
      lastSavedSnapshotRef.current = JSON.stringify(countsToSave);
      if (onTotalsChangeRef.current) {
        onTotalsChangeRef.current({
          total: sumCounts(countsToSave),
          sundayCount: result.sundayCount,
          tuesdayCount: result.tuesdayCount
        });
      }
      setSaveState('saved');

      // Sync each service that has activity to CCB (best-effort, non-blocking
      // for the saved indicator)
      const { updateCCBEventHeadCount } = await import('../firebase/functionsService');
      const liveCheckIns = checkInsByServiceRef.current || {};
      const serviceIds = Object.keys(countsToSave);
      const syncs = serviceIds
        .map((serviceId) => {
          const additionalForService = ATTENDANCE_LOCATIONS.reduce(
            (acc, loc) => acc + (countsToSave[serviceId]?.[loc.id] || 0), 0
          );
          const checkedInForService = liveCheckIns[serviceId] || 0;
          const headCount = checkedInForService + additionalForService;
          if (headCount === 0) return null;
          return updateCCBEventHeadCount({
            serviceType: serviceId,
            checkInType: 'guest',
            headCount,
            dateStr
          }).catch((err) => ({ success: false, error: err.message }));
        })
        .filter(Boolean);
      if (syncs.length > 0) {
        Promise.all(syncs).catch(() => { /* swallow — UI already showed saved */ });
      }

      // Clear "Saved" badge after a short delay
      if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
      savedClearTimerRef.current = setTimeout(() => {
        setSaveState((s) => (s === 'saved' ? 'idle' : s));
      }, 2500);
    } catch (err) {
      console.error('Failed to auto-save additional attendance:', err);
      setErrorMsg(err.message || 'Could not save');
      setSaveState('error');
    }
  }, [dateStr]);

  const scheduleSave = useCallback((nextCounts) => {
    pendingCountsRef.current = nextCounts;
    if (JSON.stringify(nextCounts) === lastSavedSnapshotRef.current) {
      // No-op change — don't schedule
      setSaveState((s) => (s === 'pending' ? 'idle' : s));
      return;
    }
    setSaveState('pending');
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const toSave = pendingCountsRef.current;
      pendingCountsRef.current = null;
      if (toSave) performSave(toSave);
    }, SAVE_DEBOUNCE_MS);
  }, [performSave]);

  const updateCount = (serviceId, locationId, value) => {
    const num = value === '' || value == null ? 0 : Math.max(0, parseInt(value, 10) || 0);
    setCounts((prev) => {
      const next = {
        ...prev,
        [serviceId]: { ...(prev[serviceId] || {}), [locationId]: num }
      };
      scheduleSave(next);
      return next;
    });
    setPulseKey(`${serviceId}|${locationId}`);
  };

  const stepCount = (serviceId, locationId, delta) => {
    setCounts((prev) => {
      const current = prev[serviceId]?.[locationId] || 0;
      const updated = Math.max(0, current + delta);
      const next = {
        ...prev,
        [serviceId]: { ...(prev[serviceId] || {}), [locationId]: updated }
      };
      scheduleSave(next);
      return next;
    });
    setPulseKey(`${serviceId}|${locationId}`);
  };

  // Flush pending save on unmount or date change
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        const toSave = pendingCountsRef.current;
        if (toSave) {
          // Fire and forget
          performSave(toSave);
        }
      }
      if (savedClearTimerRef.current) clearTimeout(savedClearTimerRef.current);
    };
  }, [dateStr, performSave]);

  if (!isServiceDay) {
    return null;
  }

  const dayLabel = day === 0 ? 'Sunday' : 'Tuesday';
  const grandTotalCheckedIn = services.reduce(
    (acc, s) => acc + (checkInsByService[s] || 0), 0
  );
  const grandTotalAdditional = sumCounts(counts);
  const grandTotal = grandTotalCheckedIn + grandTotalAdditional;

  const renderStatus = () => {
    if (saveState === 'pending') return <span className="addl-status addl-status-pending"><span className="addl-status-dot" /> Editing…</span>;
    if (saveState === 'saving') return <span className="addl-status addl-status-saving"><span className="addl-status-spinner" /> Saving…</span>;
    if (saveState === 'saved') return <span className="addl-status addl-status-saved">✓ Saved</span>;
    if (saveState === 'error') return <span className="addl-status addl-status-error">⚠️ {errorMsg || 'Save failed'}</span>;
    return <span className="addl-status addl-status-idle">Auto-saves as you change counts</span>;
  };

  return (
    <div className="addl-attendance-section">
      <div className="addl-attendance-card">
        <button
          type="button"
          className="addl-attendance-head"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          <div className="addl-attendance-head-text">
            <h3 className="addl-attendance-title">📊 Service Attendance</h3>
            <p className="addl-attendance-subtitle">
              {dayLabel} · {grandTotalCheckedIn} checked in + {grandTotalAdditional} additional = {grandTotal} total
            </p>
          </div>
          <div className="addl-attendance-head-meta">
            <div className="addl-attendance-grand-total">
              <span className="addl-grand-number">{grandTotal}</span>
              <span className="addl-grand-label">Total</span>
            </div>
          </div>
          <span className={`addl-attendance-chevron${expanded ? ' open' : ''}`} aria-hidden="true">▼</span>
        </button>

        {expanded && (loading ? (
          <div className="addl-attendance-loading">Loading…</div>
        ) : (
          <>
            <div className="addl-attendance-table" role="table">
              <div className="addl-attendance-row addl-attendance-header" role="row">
                <div className="addl-cell addl-cell-service" role="columnheader">Service</div>
                <div className="addl-cell addl-cell-checkins" role="columnheader">Checked&nbsp;In</div>
                {ATTENDANCE_LOCATIONS.map((loc) => (
                  <div key={loc.id} className="addl-cell addl-cell-location" role="columnheader">
                    + {loc.name}
                  </div>
                ))}
                <div className="addl-cell addl-cell-total" role="columnheader">Service&nbsp;Total</div>
              </div>

              {services.map((serviceId) => {
                const checkedIn = checkInsByService[serviceId] || 0;
                const additional = ATTENDANCE_LOCATIONS.reduce(
                  (acc, loc) => acc + (counts[serviceId]?.[loc.id] || 0), 0
                );
                const total = checkedIn + additional;
                return (
                  <div key={serviceId} className="addl-attendance-row" role="row">
                    <div className="addl-cell addl-cell-service" role="cell">
                      {getServiceLabel(serviceId)}
                    </div>
                    <div className="addl-cell addl-cell-checkins" role="cell">
                      <span className="addl-checked-in-pill">{checkedIn}</span>
                    </div>
                    {ATTENDANCE_LOCATIONS.map((loc) => {
                      const cellKey = `${serviceId}|${loc.id}`;
                      const value = counts[serviceId]?.[loc.id] ?? 0;
                      return (
                        <div key={loc.id} className="addl-cell addl-cell-location" role="cell">
                          <label className="addl-mobile-label">+ {loc.name}</label>
                          <div className={`addl-stepper${pulseKey === cellKey ? ' addl-stepper-pulse' : ''}`}>
                            <button
                              type="button"
                              className="addl-stepper-btn"
                              onClick={() => stepCount(serviceId, loc.id, -1)}
                              disabled={value === 0 || ro.disabled}
                              title={ro.title}
                              aria-label={`Decrease ${loc.name} count for ${getServiceLabel(serviceId)}`}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="0"
                              inputMode="numeric"
                              className="addl-stepper-input"
                              value={value}
                              onChange={(e) => updateCount(serviceId, loc.id, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              disabled={ro.disabled}
                              title={ro.title}
                              aria-label={`${loc.name} count for ${getServiceLabel(serviceId)}`}
                            />
                            <button
                              type="button"
                              className="addl-stepper-btn"
                              onClick={() => stepCount(serviceId, loc.id, 1)}
                              disabled={ro.disabled}
                              title={ro.title}
                              aria-label={`Increase ${loc.name} count for ${getServiceLabel(serviceId)}`}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="addl-cell addl-cell-total" role="cell">
                      <span className="addl-mobile-label">Service Total</span>
                      <span className="addl-service-total">{total}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {occupancyData && occupancyData.length > 0 && (() => {
              const peak = occupancyData.reduce((m, d) => Math.max(m, d.count || 0), 0);
              const hasData = occupancyData.some((d) => d.count > 0);
              return (
                <div className="addl-occupancy-section">
                  <div className="addl-occupancy-header">
                    <span className="addl-occupancy-title">Occupancy Trend</span>
                    {peak > 0 && (
                      <span className="addl-occupancy-peak">Peak: <strong>{peak}</strong></span>
                    )}
                  </div>
                  {hasData ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={occupancyData} margin={{ top: 10, right: 16, left: -8, bottom: 0 }}>
                        <defs>
                          <linearGradient id="addlOccupancyFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="#eef2f7" vertical={false} />
                        <XAxis
                          dataKey="hour"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                        />
                        <YAxis
                          allowDecimals={false}
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          width={32}
                        />
                        <Tooltip
                          cursor={{ stroke: '#c7d2fe', strokeWidth: 1 }}
                          contentStyle={{
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                            fontSize: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          }}
                          formatter={(value) => [`${value} guest${value === 1 ? '' : 's'}`, 'In building']}
                          labelStyle={{ color: '#111827', fontWeight: 600 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#6366f1"
                          strokeWidth={2}
                          fill="url(#addlOccupancyFill)"
                          activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="addl-occupancy-empty">Visitor activity will appear here throughout the day.</div>
                  )}
                </div>
              );
            })()}

            <div className="addl-attendance-footer">
              <div className="addl-attendance-summary">
                <span><strong>{grandTotalCheckedIn}</strong> checked in</span>
                <span className="addl-summary-plus">+</span>
                <span><strong>{grandTotalAdditional}</strong> additional</span>
                <span className="addl-summary-eq">=</span>
                <span><strong>{grandTotal}</strong> total</span>
              </div>
              <div className="addl-attendance-actions" aria-live="polite">
                {renderStatus()}
              </div>
            </div>
          </>
        ))}
      </div>
    </div>
  );
};

export default AdditionalAttendance;
